import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ItemForm } from '@/components/ItemForm'
import { Alert, Button, Card, PageSpinner } from '@/components/ui'
import { errorMessage } from '@/lib/pb'
import { useDeleteItem, useItems, useUpdateItem } from '@/lib/queries'

export function ItemEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: items, isPending, error } = useItems()
  const update = useUpdateItem()
  const remove = useDeleteItem()
  const [confirming, setConfirming] = useState(false)

  if (isPending) return <PageSpinner />
  if (error) return <Alert>{errorMessage(error)}</Alert>

  const existing = items?.find((item) => item.id === id)
  if (!existing) {
    return (
      <Alert>
        That record does not exist, or it is not yours.{' '}
        <button className="underline" onClick={() => navigate('/')}>
          Back to the dashboard
        </button>
      </Alert>
    )
  }

  const mutationError = update.error ?? remove.error

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-ink">Edit {existing.name}</h1>

      {confirming && (
        <Card className="space-y-3 border-overdue">
          <p className="text-sm text-ink">
            Delete <strong>{existing.name}</strong> permanently? Its reminder history goes with
            it. Prefer Archive if you only want it off the dashboard — that keeps the cost
            history.
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(existing.id, { onSuccess: () => navigate('/items') })
              }
            >
              Delete permanently
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        </Card>
      )}

      <ItemForm
        existing={existing}
        submitLabel="Save changes"
        busy={update.isPending}
        statusBusy={update.isPending}
        error={mutationError ? errorMessage(mutationError) : undefined}
        onCancel={() => navigate(-1)}
        onDelete={() => setConfirming(true)}
        onArchive={() =>
          update.mutate(
            { id: existing.id, input: { status: 'archived' } },
            { onSuccess: () => navigate('/') },
          )
        }
        onReactivate={() =>
          update.mutate(
            { id: existing.id, input: { status: 'active' } },
            { onSuccess: () => navigate('/') },
          )
        }
        onSubmit={({ input }) =>
          update.mutate({ id: existing.id, input }, { onSuccess: () => navigate('/') })
        }
      />
    </div>
  )
}
